import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { CheckCircle2, XCircle, AlertTriangle, Image as ImageIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { stringifyDetail } from '../lib/utils';
import { parseUtc } from '../lib/datetime';
import { GoalTapelligenceFooter } from '../components/goalgame/GoalTapelligenceFooter';

const API_BASE = window.location.origin;

export default function VoucherDisplay() {
    const { code } = useParams();
    const { t, i18n } = useTranslation();
    const [voucher, setVoucher] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [imageUrl, setImageUrl] = useState(null);
    const [showImage, setShowImage] = useState(false);
    const [loadingImage, setLoadingImage] = useState(false);
    // Review Mode continuation — when the guest reached this voucher via the
    // Review Shield → Gift Generator (Review Mode) flow, show a "Continue to
    // leave a public review" CTA after they've saved their gift.
    const _rcParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const reviewRedirect = _rcParams.get('rc') || '';
    const continueToReview = () => {
        window.location.href = decodeURIComponent(reviewRedirect);
    };

    useEffect(() => {
        const load = async () => {
            try {
                const res = await fetch(`${API_BASE}/api/vouchers/view/${code}`);
                if (!res.ok) {
                    const data = await res.json().catch(() => ({}));
                    throw new Error(stringifyDetail(data.detail) || 'Voucher not found');
                }
                setVoucher(await res.json());
            } catch (e) {
                setError(e.message);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [code]);

    const translateError = (msg) => {
        const map = {
            'Voucher not found': t('vouchers.voucherNotFound'),
            'EXPIRED': t('vouchers.statusExpiredLabel'),
            'VOIDED': t('vouchers.statusVoidedLabel'),
            'ALREADY REDEEMED': t('vouchers.statusRedeemedLabel'),
        };
        return map[msg] || msg;
    };

    const saveToPhotos = async () => {
        setLoadingImage(true);
        try {
            const res = await fetch(`${API_BASE}/api/vouchers/image/${code}`);
            if (!res.ok) throw new Error('Failed to generate image');
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            setImageUrl(url);
            setShowImage(true);
        } catch (e) {
            alert(t('vouchers.imageLoadFailed'));
        } finally {
            setLoadingImage(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="voucher-error">
                <div className="text-center space-y-3">
                    <AlertTriangle className="w-10 h-10 text-red-400 mx-auto" />
                    <p className="text-white font-medium">{translateError(error)}</p>
                </div>
            </div>
        );
    }

    const isActive = voucher.status === 'active';
    const isRedeemed = voucher.status === 'redeemed';
    const isExpired = voucher.status === 'expired';

    // Stripe color from voucher data (auto-assigned by reason)
    const stripeColor = voucher.stripe_color || 'green';
    const stripeClasses = {
        red: { bar: 'bg-red-500', value: 'text-red-400', accent: 'text-red-400' },
        green: { bar: 'bg-emerald-500', value: 'text-emerald-400', accent: 'text-emerald-400' },
        gold: { bar: 'gold-gradient-bar', value: 'gold-gradient-text', accent: 'gold-gradient-text' },
        blue: { bar: 'bg-blue-500', value: 'text-blue-400', accent: 'text-blue-400' },
    };
    const sc = stripeClasses[stripeColor] || stripeClasses.green;

    // Gift Generator vouchers carry the merchant's saved voucher design colors
    // (bg/text/accent). When present + active, they override the default slate
    // look so the on-screen voucher matches the saved gift-card design.
    // Non-GG vouchers have design=null → unchanged appearance (regression-safe).
    const design = (isActive && voucher.design) ? voucher.design : null;
    // When custom design colors are active, drop the gold-gradient classes —
    // their CSS `background` shorthand / `-webkit-text-fill-color: transparent`
    // would otherwise mask the inline accent color.
    const scEff = design ? { bar: '', value: '', accent: '' } : sc;
    const cardStyle = design?.bg ? { background: design.bg } : undefined;
    const accentBarStyle = design?.accent ? { background: design.accent } : undefined;
    const accentTextStyle = design?.accent ? { color: design.accent, WebkitTextFillColor: design.accent } : undefined;
    const textStyle = design?.text ? { color: design.text } : undefined;
    const mutedTextStyle = design?.text ? { color: design.text, opacity: 0.7 } : undefined;

    // Gift Generator vouchers carry a title (e.g. "2 Free Drinks") and may be
    // non-monetary (value 0). Regular vouchers have no title → unchanged.
    const hasValue = Number(voucher.value) > 0;
    const giftTitle = voucher.title;
    // P_SURVEY_GIFT_1.0 — a survey gift is a gift, never a recovery/courtesy
    // voucher. Scoped to this source; every other voucher keeps its label.
    const typeLabel = voucher.source === 'survey_gift'
        ? t('vouchers.giftLabel', 'Gift')
        : (voucher.voucher_type === 'recovery_voucher'
            ? t('vouchers.recoveryVoucher')
            : (voucher.voucher_type === 'vip'
                ? t('vouchers.vipReward', 'VIP Reward')
                : t('vouchers.courtesyVoucher')));

    // GIFT GUARD — present on prize cards that must be activated by text, and
    // absent (null) on every other voucher.
    const guard = voucher.guard || null;
    // The cut-off is the VENUE'S clock, not the phone's, so a player travelling
    // or on a phone set to another zone still reads the right time.
    const untilText = guard?.valid_until
        ? parseUtc(guard.valid_until).toLocaleString(i18n.language || 'en',
            { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
              timeZone: guard.timezone || undefined })
        : '';

    const shortFmt = { month: 'short', day: 'numeric', year: 'numeric' };
    const dateLang = i18n.language || 'en';
    const expiryShort = parseUtc(voucher.expires_at).toLocaleDateString(dateLang, shortFmt);
    const fromDate = (voucher.redeemable_from || voucher.issued_at)
        ? parseUtc(voucher.redeemable_from || voucher.issued_at).toLocaleDateString(dateLang, shortFmt)
        : '—';

    return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="voucher-display">
            <div className="w-full max-w-sm">
                {/* Voucher card */}
                <div className={`rounded-2xl overflow-hidden ${isActive ? 'bg-gradient-to-b from-slate-900 to-slate-800' : 'bg-slate-900 opacity-75'}`} style={cardStyle} data-testid="voucher-card">
                    {/* Top accent bar — stripe color */}
                    <div className={`h-2 ${isActive ? scEff.bar : isRedeemed ? 'bg-blue-500' : 'bg-red-500'}`} style={accentBarStyle} />

                    <div className="p-6 space-y-5">
                        {/* Org name / logo */}
                        <div className="text-center space-y-2">
                            {voucher.logo_url && !voucher.card_art && (
                                <img src={voucher.logo_url} alt="" className="h-12 mx-auto object-contain" />
                            )}
                            {!voucher.card_art && (
                            <p className="text-slate-400 text-sm font-medium tracking-wide" style={mutedTextStyle}>
                                {voucher.organization_name}
                            </p>
                            )}
                        </div>

                        {/* THE CARD — the very same markup the email sends, so
                            there is one design, coloured by the venue's brand.
                            It carries the value, so the plain value block below
                            is not repeated under it. */}
                        {voucher.card_art && (
                            <div data-testid="voucher-card-art"
                                 dangerouslySetInnerHTML={{ __html: voucher.card_art }} />
                        )}

                        {/* Value / gift — stripe color, primary focal point */}
                        {!voucher.card_art && (
                        <div className="text-center">
                            {hasValue ? (
                                <p className={`text-6xl font-bold leading-none ${isActive ? scEff.value : 'text-slate-500'}`} style={accentTextStyle} data-testid="voucher-value">
                                    ${voucher.value}
                                </p>
                            ) : (
                                <p className={`text-3xl font-bold leading-tight ${isActive ? scEff.value : 'text-slate-500'}`} style={accentTextStyle} data-testid="voucher-gift-title">
                                    {giftTitle || typeLabel}
                                </p>
                            )}
                            {hasValue && giftTitle && (
                                <p className="text-slate-200 text-base font-medium mt-1" style={textStyle} data-testid="voucher-gift-title">
                                    {giftTitle}
                                </p>
                            )}
                            <div className="flex items-center justify-center gap-3 mt-2">
                                <span className="h-px w-8 bg-slate-600" />
                                <p className="text-slate-400 text-sm">
                                    {typeLabel}
                                </p>
                                <span className="h-px w-8 bg-slate-600" />
                            </div>
                        </div>
                        )}

                        {/* ── Middle color band — full width stripe ── */}
                        <div className={`-mx-6 h-2.5 ${isActive ? scEff.bar : 'bg-slate-700'}`} style={accentBarStyle} />

                        {/* Code — hidden while a Gift Guard card is waiting on
                            its text: the card above shows it masked, and staff
                            cannot take it until the player has activated it. */}
                        {!(guard?.required && guard.state !== 'active') && (
                        <div className="bg-slate-800/50 rounded-xl py-4 text-center">
                            <p className={`text-3xl font-mono font-bold tracking-[0.2em] ${isActive ? 'text-white' : 'text-slate-500 line-through'}`} style={isActive ? textStyle : undefined}>
                                {voucher.code}
                            </p>
                        </div>
                        )}

                        {/* Message */}
                        {voucher.message && (
                            <p className="text-center text-slate-300 text-sm italic">
                                {voucher.message}
                            </p>
                        )}

                        {/* Status */}
                        <div className="text-center">
                            {isActive && (
                                <div className="grid grid-cols-2 gap-2" data-testid="voucher-validity-row">
                                    <div className="bg-slate-800/40 rounded-lg py-2 px-1">
                                        <p className="text-[10px] uppercase tracking-wider text-slate-500">{t('vouchers.redeemableFrom', 'Redeemable from')}</p>
                                        <p className="text-xs text-slate-200 font-medium mt-0.5">{fromDate}</p>
                                    </div>
                                    <div className="bg-slate-800/40 rounded-lg py-2 px-1">
                                        <p className="text-[10px] uppercase tracking-wider text-slate-500">{t('vouchers.expires', 'Expires')}</p>
                                        <p className="text-xs text-slate-200 font-medium mt-0.5">{expiryShort}</p>
                                    </div>
                                </div>
                            )}
                            {isRedeemed && (
                                <div className="flex items-center justify-center gap-1.5 text-blue-400">
                                    <CheckCircle2 className="w-4 h-4" />
                                    <span className="text-sm font-medium">{t('vouchers.statusRedeemedLabel')}</span>
                                </div>
                            )}
                            {isExpired && (
                                <div className="flex items-center justify-center gap-1.5 text-red-400">
                                    <XCircle className="w-4 h-4" />
                                    <span className="text-sm font-medium">{t('vouchers.statusExpiredLabel')}</span>
                                </div>
                            )}
                            {voucher.status === 'voided' && (
                                <div className="flex items-center justify-center gap-1.5 text-slate-500">
                                    <XCircle className="w-4 h-4" />
                                    <span className="text-sm font-medium">{t('vouchers.statusVoidedLabel')}</span>
                                </div>
                            )}
                        </div>

                        {/* GIFT GUARD — a money-value prize card that has to be
                            activated by text before staff may accept it. The
                            expiry is stated plainly ABOVE the button, and no QR
                            is shown until the card is live. */}
                        {guard?.required && (
                            <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 text-center space-y-3"
                                 data-testid="guard-block">
                                {guard.state === 'not_activated' && (
                                    <>
                                        {/* THE ORDER THE PLAYER READS IT IN: when
                                            the card runs out, that it is not
                                            activated, the button, then the
                                            3:00 AM line. */}
                                        <p className="text-sm text-slate-200" data-testid="guard-expiry">
                                            {t('giftGuard.cardExpires', { when: expiryShort })}
                                        </p>
                                        <p className="text-xs font-bold uppercase tracking-wider text-amber-300"
                                           data-testid="guard-status">{t('giftGuard.notActivated')}</p>
                                        {voucher.activation?.sms_link ? (
                                            <>
                                                <a href={voucher.activation.sms_link}
                                                   data-testid="guard-activate-btn"
                                                   className="inline-flex min-h-[48px] w-full items-center justify-center rounded-xl bg-amber-500 px-5 text-base font-bold text-slate-900">
                                                    {t('giftGuard.activateByText')}
                                                </a>
                                                <p className="text-sm font-semibold text-white" data-testid="guard-validity">
                                                    {t('giftGuard.validityLine')}
                                                </p>
                                                <p className="text-[11px] text-slate-400" data-testid="guard-activate-hint">
                                                    {t('giftGuard.activateHint')}
                                                </p>
                                            </>
                                        ) : (
                                            <p className="text-xs text-slate-400" data-testid="guard-no-number">
                                                {t('giftGuard.noNumberYet')}
                                            </p>
                                        )}
                                    </>
                                )}
                                {guard.state === 'active' && (
                                    <p className="text-sm font-semibold text-emerald-300" data-testid="guard-active">
                                        {t('giftGuard.activeUntil', { when: untilText })}
                                    </p>
                                )}
                                {(guard.state === 'void' || guard.state === 'expired') && (
                                    <p className="text-sm font-semibold text-red-300" data-testid="guard-over">
                                        {t('giftGuard.windowOver')}
                                    </p>
                                )}
                                {voucher.booking?.show && (
                                    <a href={voucher.booking.url} target="_blank" rel="noopener noreferrer"
                                       data-testid="guard-booking-btn"
                                       className="inline-flex min-h-[44px] w-full items-center justify-center rounded-xl border border-emerald-400/60 px-5 text-sm font-bold text-emerald-300">
                                        {t('giftGuard.book')}
                                    </a>
                                )}
                            </div>
                        )}

                        {/* QR — compact redemption strip (lookup only, staff PIN still required) */}
                        {isActive && voucher.qr_token && (
                            <div className="rounded-xl bg-white flex items-center gap-3 p-2.5 text-left" data-testid="voucher-qr-panel">
                                <QRCodeSVG
                                    value={`${API_BASE}/voucher/scan/${voucher.qr_token}`}
                                    size={100}
                                    level="M"
                                    marginSize={2}
                                    className="shrink-0"
                                    data-testid="voucher-qr-code"
                                />
                                <div className="flex-1 min-w-0">
                                    <p className="text-slate-900 font-bold text-xs uppercase tracking-wide">
                                        {t('vouchers.scanToRedeem', 'Scan to Redeem')}
                                    </p>
                                    <p className="text-slate-500 text-[11px] mt-1 leading-snug">
                                        {t('vouchers.scanStripNote', 'Staff scan · one-time use')}
                                    </p>
                                    <div className={`mt-2 h-1 w-10 rounded-full ${stripeColor === 'red' ? 'bg-red-400' : stripeColor === 'blue' ? 'bg-blue-400' : stripeColor === 'gold' ? 'bg-amber-400' : 'bg-emerald-400'}`} />
                                </div>
                            </div>
                        )}

                        {/* Location */}
                        {voucher.location_name && (
                            <p className="text-center text-xs text-slate-500">
                                <span className="uppercase tracking-wider text-[10px] text-slate-600">{t('vouchers.validAt', 'Valid at')}</span>
                                {' '}
                                {voucher.location_name}
                            </p>
                        )}
                    </div>

                    {/* Bottom accent — stripe color */}
                    <div className={`h-1 ${isActive ? scEff.bar : isRedeemed ? 'bg-blue-500' : 'bg-red-500'}`} style={accentBarStyle} />
                </div>

                {/* Save button */}
                {isActive && !showImage && !(guard && guard.state !== 'active') && (
                    <div className="mt-4">
                        <Button
                            data-testid="save-voucher-btn"
                            onClick={saveToPhotos}
                            disabled={loadingImage}
                            className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 gap-2"
                        >
                            <ImageIcon className="w-4 h-4" />
                            {loadingImage ? t('common.loading') : t('vouchers.saveToPhotos')}
                        </Button>
                        <p className="text-center text-xs text-slate-500 mt-2">
                            {t('vouchers.saveHint')}
                        </p>
                    </div>
                )}

                {/* Image display fallback — long-press is a SECONDARY save path */}
                {showImage && imageUrl && (
                    <div className="mt-4 space-y-3" data-testid="voucher-image-display">
                        <p className="text-center text-xs text-slate-400">
                            {t('vouchers.longPressHint')}
                        </p>
                        <img
                            src={imageUrl}
                            alt={`Voucher ${voucher.code}`}
                            className="w-full rounded-lg shadow-lg"
                            data-testid="voucher-image"
                        />
                        <Button
                            variant="ghost"
                            className="w-full text-slate-400 hover:text-white text-sm"
                            onClick={() => { setShowImage(false); if (imageUrl) URL.revokeObjectURL(imageUrl); setImageUrl(null); }}
                        >
                            {t('vouchers.closeImage')}
                        </Button>
                    </div>
                )}

                {/* Review Mode — continue to public review after saving the gift */}
                {isActive && reviewRedirect && (
                    <div className="mt-3">
                        <Button
                            data-testid="continue-to-review-btn"
                            onClick={continueToReview}
                            className="w-full h-12 bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-500 hover:brightness-110 text-slate-950 font-bold gap-2"
                        >
                            {t('giftGenerator.continueToReview')}
                        </Button>
                    </div>
                )}

                {/* A Gift Guard card waiting on its text must never be told to
                    show anything to staff — it is worth nothing until the
                    player activates it. */}
                <p className="text-center text-xs text-slate-600 mt-4">
                    {guard?.required && guard.state !== 'active'
                        ? t('giftGuard.validityLine')
                        : t('vouchers.presentToStaff')}
                </p>

                {/* A prize card is a player page, so it carries the same small
                    Tapelligence line. */}
                {guard?.required && <GoalTapelligenceFooter />}
            </div>
        </div>
    );
}
